import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import "./ChatbotWidget.css";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const ChatbotWidget = () => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm Kashish, your Hostel Management Assistant. How can I help you today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async (e) => {
    e.preventDefault();

    if (!inputValue.trim() || isLoading) return; // LOCK

    setIsLoading(true); // LOCK immediately
    const messageToSend = inputValue;
    setInputValue("");

    const userMessage = {
      id: messages.length + 1,
      text: messageToSend,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:5000";
      const { data } = await axios.post(
        `${apiUrl}/api/chatbot/message`,
        { message: messageToSend },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const botMessage = {
        id: messages.length + 2,
        text: data.response?.response || data.response || "No response",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const botMessage = {
        id: messages.length + 2,
        text:
          error.response?.data?.message ||
          "Sorry, something went wrong. Please try again.",
        sender: "bot",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, botMessage]);
    } finally {
      setIsLoading(false); // Unlock
    }
  };

  const clearHistory = async () => {
    if (window.confirm("Are you sure you want to clear the chat history?")) {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:5000";
        await axios.delete(`${apiUrl}/api/chatbot/history`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setMessages([
          {
            id: 1,
            text: "Hello! I'm your Hostel Management Assistant. How can I help you today?",
            sender: "bot",
            timestamp: new Date(),
          },
        ]);
      } catch (error) {
        console.error("Error clearing history:", error);
      }
    }
  };

  return (
    <div className="chatbot-widget">
      {/* Chatbot Button */}
      {!isOpen && (
        <button
          className="chatbot-toggle-btn"
          onClick={() => setIsOpen(true)}
          title="Open Chatbot"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>Kashish Assistant</h3>
            <div className="header-actions">
              <button
                className="icon-btn clear-btn"
                onClick={clearHistory}
                title="Clear chat"
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </button>

              <button
                className="icon-btn close-btn"
                onClick={() => setIsOpen(false)}
                title="Close chat"
              >
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`message ${msg.sender} ${
                  msg.isError ? "error" : ""
                }`}
              >
                <div className="message-content">
                  <p>{msg.text}</p>
                  <span className="message-time">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message bot">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="chatbot-input-area" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !inputValue.trim()}>
              {isLoading ? "..." : <SendRoundedIcon fontSize="small" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;